import { authSplitApi } from 'redux/helpers/slice.helpers';
import { queryParamConverter } from 'data/converters/general.converters';
import {
  accessCodeConverter,
  accessCodeRowConverter,
  accessCodesTableConverter,
  coveringAccessCodesConverter,
} from 'data/converters/accessCode.converters';
import {
  TAccessCodeRowDB,
  TAccessCodeRowUI,
  TAccessCodesTableDB,
  TAccessCodesTableUI,
  TCoveringAccessCodesDB,
  TCoveringAccessCodesUI,
  TCreateAccessCodeRequestUI,
  TGetAccessCodeDB,
  TGetAccessCodeUI,
  TUpdateAccessCodeRequestUI,
} from 'data/types/accessCode.types';
import { TPageableDataParamUI } from 'data/types/pagableData.type';
import { EAccessCodeUrls } from 'data/urls/accessCode.url';
import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';

const baseUrl = `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EAccessCodeUrls.ACCESS_CODE}`;

export const accessCodeApi = authSplitApi('accessCode', [
  'accessCodes',
  'accessCode',
  'propertyAccessCodes',
]).injectEndpoints({
  endpoints: (build) => ({
    getAccessCodes: build.query<TAccessCodesTableUI, TPageableDataParamUI>({
      query({ search, pagination }) {
        return {
          url: baseUrl,
          method: 'GET',
          params: queryParamConverter(search, pagination),
        };
      },
      transformResponse: (data: TAccessCodesTableDB) => accessCodesTableConverter.fromDb(data),
      providesTags: ['accessCodes'],
    }),
    getCoveringAccessCodes: build.query<TCoveringAccessCodesUI, { propertyIds: string[] }>({
      query({ propertyIds }) {
        return {
          url: `${baseUrl}/covering`,
          method: 'GET',
          params: { propertyIds },
        };
      },
      transformResponse: (data: TCoveringAccessCodesDB) => coveringAccessCodesConverter.fromDb(data),
      providesTags: ['accessCodes'],
    }),
    getAccessCodesByProperty: build.query<TAccessCodeRowUI[], { propertyId: string }>({
      query({ propertyId }) {
        return {
          url: `${baseUrl}/by-property/${propertyId}`,
          method: 'GET',
        };
      },
      // The "All Properties" default always renders first.
      transformResponse: (data: TAccessCodeRowDB[]) =>
        (data || [])
          .map((item) => accessCodeRowConverter.fromDb(item))
          .sort((a, b) => Number(b.isAllProperties) - Number(a.isAllProperties)),
      providesTags: ['propertyAccessCodes'],
    }),
    removePropertyFromAccessCode: build.mutation<void, { accessCodeId: string; propertyId: string }>({
      query({ accessCodeId, propertyId }) {
        return {
          url: `${baseUrl}/${accessCodeId}/property/${propertyId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['propertyAccessCodes', 'accessCodes'],
    }),
    getAccessCode: build.query<TGetAccessCodeUI, { accessCodeId: string }>({
      query({ accessCodeId }) {
        return {
          url: `${baseUrl}/${accessCodeId}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TGetAccessCodeDB) => accessCodeConverter.fromDb(data),
      providesTags: (result, error, { accessCodeId }) => [{ type: 'accessCode', id: accessCodeId }],
    }),
    createAccessCode: build.mutation<string, TCreateAccessCodeRequestUI>({
      query(body) {
        return {
          url: baseUrl,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['accessCodes', 'propertyAccessCodes'],
    }),
    updateAccessCode: build.mutation<string, TUpdateAccessCodeRequestUI>({
      query(body) {
        return {
          url: baseUrl,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: (result, error, { accessCodeId }) => [
        'accessCodes',
        'propertyAccessCodes',
        { type: 'accessCode', id: accessCodeId },
      ],
    }),
    updateAllPropertiesCodeVisibility: build.mutation<void, { hidden: boolean }>({
      query(body) {
        return {
          url: `${baseUrl}/all-properties-visibility`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['accessCodes', 'propertyAccessCodes'],
    }),
    deleteAccessCode: build.mutation<void, { accessCodeId: string }>({
      query({ accessCodeId }) {
        return {
          url: baseUrl,
          method: 'DELETE',
          body: { accessCodeId },
        };
      },
      invalidatesTags: ['accessCodes', 'propertyAccessCodes'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAccessCodesQuery,
  useGetAccessCodeQuery,
  useLazyGetCoveringAccessCodesQuery,
  useGetAccessCodesByPropertyQuery,
  useCreateAccessCodeMutation,
  useUpdateAccessCodeMutation,
  useUpdateAllPropertiesCodeVisibilityMutation,
  useDeleteAccessCodeMutation,
  useRemovePropertyFromAccessCodeMutation,
} = accessCodeApi;
